import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Award, Users, Target, Heart, CheckCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getStatsArray } from "@/config/companyStats";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Target,
  Users,
  Heart,
  Award
};

export default function About() {
  const { t, i18n } = useTranslation('about');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Handle values - can be array or object
  const valuesRaw = t('values.items', { returnObjects: true });
  const values = Array.isArray(valuesRaw) 
    ? valuesRaw as Array<{ icon: string; title: string; description: string }>
    : Object.values(valuesRaw as Record<string, { title: string; description: string }>).map((v, i) => ({
        icon: ['Target', 'Users', 'Heart', 'Award'][i] || 'Target',
        title: v.title,
        description: v.description
      }));

  // Get achievements from central config
  const achievements = getStatsArray(i18n.language === 'he' ? 'he' : 'en');

  // Handle story content - can be array or individual paragraphs
  const storyRaw = t('story.content', { returnObjects: true });
  const storyContent = Array.isArray(storyRaw) 
    ? storyRaw as string[]
    : [t('story.paragraph1'), t('story.paragraph2'), t('story.paragraph3')].filter(Boolean);

  const whyUsItems = t('whyUs.items', { returnObjects: true }) as string[];

  return (
    <Layout>
      <Helmet>
        <title>אודות אדם בטיחות – הצוות, הניסיון והגישה</title>
        <meta name="description" content="הכירו את אדם בטיחות: צוות ממוני בטיחות עם הסמכות NEBOSH ו-CMSE, ניסיון בתשתיות, בנייה ותעשייה, וגישה אישית לכל לקוח." />
        <link rel="canonical" href="https://adam-safety.com/about" />
        <meta property="og:title" content="אודות אדם בטיחות – הצוות, הניסיון והגישה" />
        <meta property="og:description" content="צוות ממוני בטיחות עם הסמכות בינלאומיות, ניסיון רחב וגישה אישית לכל ארגון." />
        <meta property="og:url" content="https://adam-safety.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-primary">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/20" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-accent/50 rounded-full blur-3xl" />
        </div>
        
        <div className="container relative z-10 px-4 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-accent/20 text-accent text-sm font-semibold mb-6">
              {t('hero.badge')}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className={`grid lg:grid-cols-2 gap-16 items-center ${!isRTL ? 'lg:grid-flow-dense' : ''}`}>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={!isRTL ? 'lg:col-start-1' : ''}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                {t('story.title')}
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {storyContent.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={`grid grid-cols-2 gap-4 ${!isRTL ? 'lg:col-start-2' : ''}`}
            >
              {achievements.map((item, index) => (
                <div
                  key={index}
                  className="bg-card rounded-2xl p-6 text-center border border-border"
                >
                  <div className="text-4xl font-bold text-accent mb-2">{item.number}</div>
                  <div className="text-sm text-muted-foreground">{item.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-secondary">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('values.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('values.subtitle')}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon] || Target;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-8 text-center border border-border hover:border-accent/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8">
                {t('whyUs.title')}
              </h2>
              <div className="space-y-6">
                {whyUsItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-accent" />
                    </div>
                    <span className="text-foreground font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-primary rounded-2xl p-10 text-center"
            >
              <h3 className="font-heading text-2xl font-bold text-primary-foreground mb-4">
                {t('whyUs.cta.title')}
              </h3>
              <p className="text-primary-foreground/80 mb-8">
                {t('whyUs.cta.subtitle')}
              </p>
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2">
                  {t('whyUs.cta.button')}
                  <ArrowIcon className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
